import { useEffect, useState } from 'react';
import { erfuelltRecht } from '@symphenia/shared-types';
import { useAuthStore } from '@/lib/auth.store';
import { fetchClosingSetting, setClosingSetting, type AbschlussBearbeitung } from '../api/payroll.api';

const STUFEN: { id: AbschlussBearbeitung; label: string; hinweis: string }[] = [
  {
    id: 'LOCKED',
    label: 'Gesperrt',
    hinweis: 'Ein fertiggestellter Monat kann nur noch gelesen werden.',
  },
  {
    id: 'REASON',
    label: 'Nur mit Grund',
    hinweis:
      'Ändern geht über „Monat ändern" — mit einem Grund (z. B. „Stunden am 04.09. nachgetragen"). Wer, wann, alt und neu werden festgehalten.',
  },
  {
    id: 'FREE',
    label: 'Frei änderbar',
    hinweis: 'Ändern wie in einem offenen Monat. Die Änderungen werden trotzdem festgehalten, ohne Grund.',
  },
];

/**
 * Einstellung „Fertiggestellte Monate" (AP-22 Teil 2, G2).
 *
 * **Kein Rechenwert:** Sie gilt ab sofort für alle Monate und steht deshalb
 * neben den Einstellungen mit Gültig-ab, nicht darin. Ändern darf sie nur, wer
 * `payroll.closing.configure` hat — der Administrator der Firma, nicht die
 * Rolle „Lohnverrechnung". Eine Auswahl speichert sofort (AP-36 Fall 1).
 */
export function AbschlussEinstellung() {
  const rechte = useAuthStore((s) => s.user?.permissions);
  const darfLesen = erfuelltRecht('payroll.month.read', rechte);
  const darfAendern = erfuelltRecht('payroll.closing.configure', rechte);
  const [modus, setModus] = useState<AbschlussBearbeitung | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!darfLesen) return;
    fetchClosingSetting()
      .then(setModus)
      .catch(() => setError('Die Einstellung konnte nicht geladen werden.'));
  }, [darfLesen]);

  if (!darfLesen) return null;

  const waehle = async (neu: AbschlussBearbeitung) => {
    const vorher = modus;
    setModus(neu);
    setError(null);
    try {
      setModus(await setClosingSetting(neu));
    } catch {
      setModus(vorher);
      setError('Die Einstellung konnte nicht gespeichert werden.');
    }
  };

  return (
    <div className="space-y-3 rounded-lg border border-border bg-card p-4 shadow-sm">
      <div>
        <h2 className="text-sm font-semibold text-foreground">Fertiggestellte Monate</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Was mit einem Monat geschehen darf, nachdem er fertiggestellt wurde. Gilt ab sofort für alle Monate
          {darfAendern ? '.' : ' — die Einstellung ändert der Administrator der Firma.'}
        </p>
      </div>
      <div className="space-y-2">
        {STUFEN.map((s) => (
          <label key={s.id} className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name="abschluss-bearbeitung"
              className="mt-0.5 h-4 w-4 accent-[hsl(var(--primary))]"
              checked={modus === s.id}
              disabled={!darfAendern || modus === null}
              onChange={() => void waehle(s.id)}
            />
            <span>
              <span className="text-foreground">{s.label}</span>
              <span className="block text-xs text-muted-foreground">{s.hinweis}</span>
            </span>
          </label>
        ))}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
